import { deepCopy } from "@/fx/data-helpers";
import { SAMPLE_DATA } from "./sample-data";
import { CLM } from "../../common/schema/claims";
import { InMemoryRecords } from "@/fx/record-handling";
import assert from "assert";
import { CTX } from "../context";

export class InMemoryDataRepository
{
  private readonly CTX: CTX.Service

  private readonly globalSettings_: CLM.GlobalSettings = {};

  constructor({ dataGenerator, CTX }: {
    dataGenerator: SAMPLE_DATA.DataGenerator,
    CTX:           CTX.Service
  })
  {
    this.wholesalerRecords.insertAll(dataGenerator.seedWholesalers);

    this.CTX = CTX;
  }
  readonly globalSettings = () => deepCopy(this.globalSettings_);

  readonly dataContext = ({ currentUser }: { currentUser: CLM.User }) => {

    return (() => {

      assert(this.initializationComplete);

      const now                      = new Date();
      const globalSettings           = this.globalSettings();
      const wholesalerBySapIdIfExist = this.wholesalerBySapIdIfExist;

      return { now, currentUser, globalSettings, wholesalerBySapIdIfExist };

    }).bind(this);
  }

  readonly claimCleanupContext = async () => {
    const { currentUser } = await this.CTX.loggedInContext();

    return CLM.claimCleanupContext(this.dataContext({ currentUser }));
  }
  readonly fromRawClaim = async () => CLM.fromRawClaim(
    await this.claimCleanupContext()
  );
  readonly toRawClaim  = CLM.toRawClaim;
  readonly toRawClaims = (claims: CLM.Claim[]) => claims.map(this.toRawClaim);

  readonly fromRawUlca = CLM.fromRawUlca;
  readonly toRawUlca   = CLM.toRawUlca;

  readonly fromRawIssue = CLM.fromRawIssue;
  readonly toRawIssue   = CLM.toRawIssue;

  readonly toRawDistributor  = CLM.toRawDistributor;
  readonly toRawDistributors = (distributors: CLM.Distributor[]) => distributors.map(this.toRawDistributor);

  readonly toRawDistributorLocation  = CLM.toRawDistributorLocation;
  readonly toRawDistributorLocations = (locations: CLM.DistributorLocation[]) => locations.map(this.toRawDistributorLocation);

  readonly fromRawProductLineItem = CLM.fromRawProductLineItem;
  readonly toRawProductLineItem   = CLM.toRawProductLineItem;

  readonly fromRawProduct = CLM.fromRawProduct;
  readonly toRawProduct   = CLM.toRawProduct;

  readonly fromRawBbcEmployee = CLM.fromRawBbcEmployee;
  readonly toRawBbcEmployee   = CLM.toRawBbcEmployee;

  readonly fromRawUser = CLM.fromRawUser;
  readonly toRawUser   = CLM.toRawUser;

  readonly wholesalerRecords = new InMemoryRecords<CLM.WholesalerId, CLM.Wholesaler>({});

  readonly wholesalers = this.wholesalerRecords.all.bind(this.wholesalerRecords);

  readonly wholesaler = this.wholesalerRecords.single.bind(this.wholesalerRecords);

  readonly wholesalerBySapIdIfExist = (sapId: string) => {
    if (sapId === undefined) throw new Error(`Parameter sapId must be defined. It is ${sapId}.`);
    if (sapId === null     ) throw new Error(`Parameter sapId must be defined. It is ${sapId}.`);

    return this.wholesalers().find(_ => _.sapId === sapId);
  }

  private readonly initializationComplete = true;
}

export async function inMemoryDataRepository({ CTX, dataGenerator }: {
  CTX:           CTX.Service
  dataGenerator: SAMPLE_DATA.DataGenerator
}) : Promise<InMemoryDataRepository> {
  return new InMemoryDataRepository({ dataGenerator, CTX });
}
